import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  Button,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Assessment as InsightsIcon,
  TableChart as AdsManagerIcon,
  Create as StudioIcon,
  Code as DevelopersIcon,
  BarChart as CampaignAnalyticsIcon,
  Build as OptimizerIcon,
  Gavel as RulesIcon,
  Timeline as RulesDashboardIcon,
  Search as RulesExplorerIcon,
  Psychology as CampaignGeniusIcon,
  Image as MediaLibraryIcon,
  CloudDownload as ScraperIcon,
  AttachMoney as RSOCKeywordsIcon,
  Shield as ComplianceIcon,
  MoreHoriz as MoreIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

const TopBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [visibleItems, setVisibleItems] = useState<number>(5); // Start conservative to avoid blink
  const [isCalculated, setIsCalculated] = useState<boolean>(false);
  const navRef = useRef<HTMLDivElement>(null);

  const menuItems = [
    { text: 'Dashboard', path: '/', icon: <DashboardIcon sx={{ fontSize: 18 }} /> },
    { text: 'Insights', path: '/insights', icon: <InsightsIcon sx={{ fontSize: 18 }} /> },
    { text: 'Ads Manager', path: '/ads-manager', icon: <AdsManagerIcon sx={{ fontSize: 18 }} /> },
    { text: 'Studio', path: '/studio', icon: <StudioIcon sx={{ fontSize: 18 }} /> },
    { text: 'For Developers', path: '/developers', icon: <DevelopersIcon sx={{ fontSize: 18 }} /> },
    { text: 'Campaign Analytics', path: '/campaign-analytics', icon: <CampaignAnalyticsIcon sx={{ fontSize: 18 }} /> },
    { text: 'Optimizer', path: '/optimizer', icon: <OptimizerIcon sx={{ fontSize: 18 }} /> },
    { text: 'Rules', path: '/rules', icon: <RulesIcon sx={{ fontSize: 18 }} /> },
    { text: 'Rules Dashboard', path: '/rules-dashboard', icon: <RulesDashboardIcon sx={{ fontSize: 18 }} /> },
    { text: 'Rules Explorer', path: '/rules-explorer', icon: <RulesExplorerIcon sx={{ fontSize: 18 }} /> },
    { text: 'Campaign Genius', path: '/campaign-genius', icon: <CampaignGeniusIcon sx={{ fontSize: 18 }} /> },
    { text: 'Media Library', path: '/media-library', icon: <MediaLibraryIcon sx={{ fontSize: 18 }} /> },
    { text: 'Scraper', path: '/scraper', icon: <ScraperIcon sx={{ fontSize: 18 }} /> },
    { text: 'RSOC Keywords', path: '/rsoc-keywords', icon: <RSOCKeywordsIcon sx={{ fontSize: 18 }} /> },
    { text: 'Compliance', path: '/compliance', icon: <ComplianceIcon sx={{ fontSize: 18 }} /> },
  ];

  useEffect(() => {
    const calculateVisibleItems = () => {
      if (!navRef.current) return;

      const containerWidth = navRef.current.offsetWidth;
      const itemWidth = 140; // Approximate width per menu item
      const moreButtonWidth = 100; // Width for "More" button

      const possibleItems = Math.floor((containerWidth - moreButtonWidth) / itemWidth);
      const newVisibleItems = Math.max(3, Math.min(possibleItems, menuItems.length));

      setVisibleItems(newVisibleItems);
      setIsCalculated(true); // Mark as calculated to show menu
    };

    // Initial calculation with small delay to ensure DOM is ready
    const initialTimeout = setTimeout(() => {
      calculateVisibleItems();
    }, 50);

    // Use ResizeObserver for more reliable tracking
    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleItems();
    });

    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    // Fallback to window resize
    window.addEventListener('resize', calculateVisibleItems);

    return () => {
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateVisibleItems);
    };
  }, [menuItems.length]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  const handleSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  const handleMenuItemClick = (path: string) => {
    handleMoreMenuClose();
    navigate(path);
  };

  const visibleMenuItems = menuItems.slice(0, visibleItems);
  const overflowMenuItems = menuItems.slice(visibleItems);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: '#FFFFFF',
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Toolbar sx={{ minHeight: '63px !important', padding: '0 30px', gap: '40px' }}>
        {/* Left side: Logo & Brand */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            gap: '16px',
          }}
          onClick={() => navigate('/')}
        >
          <Box
            component="img"
            src="/logoclearblack.png"
            alt="Logo"
            sx={{
              height: 30,
              width: 'auto',
            }}
          />

          {/* Vertical Divider */}
          <Box
            sx={{
              width: '1px',
              height: '40px',
              backgroundColor: theme.palette.divider,
            }}
          />

          {/* Brand Text */}
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontWeight: 600,
                color: theme.palette.primary.main,
                lineHeight: 1.2,
              }}
            >
              Business Intelligence
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: theme.palette.text.primary,
                lineHeight: 1.2,
                marginTop: '2px',
              }}
            >
              Dashboard
            </Typography>
          </Box>
        </Box>

        {/* Center: Navigation Menu */}
        <Box
          ref={navRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flex: 1,
            opacity: isCalculated ? 1 : 0,
            transition: 'opacity 0.15s ease-in',
          }}
        >
          {visibleMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.text}
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                sx={{
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  fontWeight: isActive ? 600 : 500,
                  fontSize: '13px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '08',
                    color: theme.palette.primary.main,
                  },
                  ...(isActive && {
                    backgroundColor: theme.palette.primary.main + '10',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '12px',
                      right: '12px',
                      height: '2px',
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '2px 2px 0 0',
                    },
                  }),
                }}
              >
                {item.text}
              </Button>
            );
          })}

          {/* More dropdown for overflow items */}
          {overflowMenuItems.length > 0 && (
            <>
              <Button
                startIcon={<MoreIcon />}
                onClick={handleMoreMenuOpen}
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: '13px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '08',
                    color: theme.palette.primary.main,
                  },
                }}
              >
                More
              </Button>
              <Menu
                anchorEl={moreAnchorEl}
                open={Boolean(moreAnchorEl)}
                onClose={handleMoreMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                slotProps={{
                  paper: {
                    sx: {
                      marginTop: '8px',
                      minWidth: '200px',
                      borderRadius: '8px',
                      boxShadow: '0 5px 20px rgba(146,153,184,0.15)',
                    },
                  },
                }}
              >
                {overflowMenuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <MenuItem
                      key={item.text}
                      onClick={() => handleMenuItemClick(item.path)}
                      sx={{
                        padding: '10px 16px',
                        fontSize: '14px',
                        color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                        fontWeight: isActive ? 600 : 400,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main + '08',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {item.icon}
                        {item.text}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
        </Box>

        {/* Right side: Admin Console, Settings & Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Admin Console Button */}
          <Button
            startIcon={<AdminIcon />}
            onClick={() => navigate('/admin')}
            sx={{
              color: location.pathname === '/admin' ? theme.palette.primary.main : theme.palette.text.secondary,
              fontWeight: location.pathname === '/admin' ? 600 : 500,
              fontSize: '13px',
              padding: '6px 12px',
              borderRadius: '6px',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: theme.palette.primary.main + '08',
                color: theme.palette.primary.main,
              },
              ...(location.pathname === '/admin' && {
                backgroundColor: theme.palette.primary.main + '10',
              }),
            }}
          >
            Admin Console
          </Button>

          {/* Profile Button */}
          <IconButton
            size="large"
            aria-label="account"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                backgroundColor: theme.palette.primary.main,
                fontSize: '16px',
                fontWeight: 600,
              }}
            >
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>

          {/* Profile Menu */}
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                sx: {
                  marginTop: '8px',
                  minWidth: '200px',
                  borderRadius: '8px',
                  boxShadow: '0 5px 20px rgba(146,153,184,0.15)',
                },
              },
            }}
          >
            <Box sx={{ padding: '12px 16px', borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {user?.name || user?.email}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {user?.role}
              </Typography>
            </Box>
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
              <PersonIcon sx={{ marginRight: '12px', fontSize: '20px' }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleSettings}>
              <SettingsIcon sx={{ marginRight: '12px', fontSize: '20px' }} />
              Settings
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
              <LogoutIcon sx={{ marginRight: '12px', fontSize: '20px' }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
